/**
 * SAS and stored-policy dates (review C F4, review D RD4). `<input type="datetime-local">` shows and edits LOCAL
 * wall-clock time, while requests and ACLs carry UTC ISO instants. Both dialogs convert through this module only
 * (via `LocalDateTimeField`), with ONE rule for daylight-saving transitions:
 * - a wall time that does not exist (the spring-forward gap) resolves to `gap` — the field refuses it visibly and the
 *   dialog cannot Generate / Save until a real time is entered; it is never silently moved;
 * - a wall time that occurs twice (the fall-back fold) keeps the UTC offset the field's current instant already has —
 *   so an in-fold edit never jumps to the other occurrence — and with nothing to keep takes the EARLIER occurrence
 *   (ECMAScript's own choice); the field shows the UTC offset and offers the other occurrence (`other`).
 * An unedited value is never re-resolved, so an existing grant is sent back unchanged.
 */
const pad = (value: number): string => String(value).padStart(2, '0');
const DAY_MS = 86_400_000;
const WALL_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/u;

export type LocalResolution =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'gap' }
  | { kind: 'ok'; iso: string; other?: string };

export function isoToLocalInput(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Minutes east of UTC in force at that instant (New York in summer: -240). */
const offsetMinutes = (ms: number): number => -new Date(ms).getTimezoneOffset();

export function utcOffsetLabel(iso: string): string {
  const minutes = offsetMinutes(Date.parse(iso));
  const absolute = Math.abs(minutes);
  return `UTC${minutes < 0 ? '-' : '+'}${pad(Math.trunc(absolute / 60))}:${pad(absolute % 60)}`;
}

/** Every instant whose local wall-clock rendering is `text`, earliest first: none in a gap, two in a fold. */
function instantsFor(text: string): number[] | undefined {
  const match = WALL_TIME.exec(text);
  if (!match) return undefined;
  const [year, month, day, hour, minute, second] = match.slice(1).map((part) => Number(part ?? 0));
  const wall = Date.UTC(year!, month! - 1, day!, hour!, minute!, second!);
  // Transitions are months apart, so the offsets in force a day either side cover every candidate.
  const offsets = new Set([wall - DAY_MS, wall, wall + DAY_MS].map(offsetMinutes));
  const minuteText = text.slice(0, 16);
  const candidates = [...offsets].map((offset) => wall - offset * 60_000).filter((ms) => isoToLocalInput(new Date(ms).toISOString()) === minuteText);
  return [...new Set(candidates)].sort((left, right) => left - right);
}

/** `keepOffsetOf`: the field's current instant, whose UTC offset a repeated wall time keeps. */
export function resolveLocalInput(text: string, keepOffsetOf?: string): LocalResolution {
  if (!text) return { kind: 'empty' };
  const instants = instantsFor(text);
  if (!instants) return { kind: 'invalid' };
  if (instants.length === 0) return { kind: 'gap' };
  const keep = keepOffsetOf && Number.isFinite(Date.parse(keepOffsetOf)) ? offsetMinutes(Date.parse(keepOffsetOf)) : undefined;
  const chosen = instants.find((ms) => offsetMinutes(ms) === keep) ?? instants[0]!;
  const other = instants.find((ms) => ms !== chosen);
  return { kind: 'ok', iso: new Date(chosen).toISOString(), ...(other === undefined ? {} : { other: new Date(other).toISOString() }) };
}

export const LOCAL_TIME_HINT = 'local time';
export const LOCAL_TIME_GAP_MESSAGE = 'This local time does not exist — the clock skips it for daylight saving. Choose another time.';
export const LOCAL_TIME_INVALID_MESSAGE = 'Enter a complete date and time.';
